import { useEffect, useRef } from 'react';

type Params = {
  messageCount: number;
  /** 最後の AI 応答メッセージの id。AI 応答がまだない・末尾が user メッセージのときは undefined */
  lastAssistantId: string | undefined;
  /**
   * AI 応答待ち中フラグ。`isLoading` が立った瞬間（送信直後）にも下端スクロールを発火させる
   * ために deps に含める。送信直後は messages の refetch 前なので messageCount /
   * lastAssistantId だけでは effect が動かず、LoadingMessage が画面外に出るのを防ぐ。
   */
  isLoading: boolean;
};

/**
 * AI チャット一覧のスクロール挙動を制御するフック。
 *
 * - 初回マウント（スレッドを開いた直後）: `bottomMarkerRef` を最下部にスクロール
 * - 新しい AI 応答が到着したとき: `lastAssistantRef`（最後の assistant メッセージ）を画面トップにスクロール
 * - それ以外の更新（ユーザー送信・LoadingMessage 表示等）: `bottomMarkerRef` を最下部にスクロール
 *
 * スレッド切替時にスクロール状態をリセットしたい場合は、呼び出し側で `key={threadId}` を
 * 付けて当該コンポーネントごと再マウントすること（このフック単体ではスレッドを判別しない）。
 */
export const useChatScrollAnchor = ({ messageCount, lastAssistantId, isLoading }: Params) => {
  const lastAssistantRef = useRef<HTMLDivElement | null>(null);
  const bottomMarkerRef = useRef<HTMLDivElement | null>(null);
  const isInitialRef = useRef(true);
  const prevAssistantIdRef = useRef<string | undefined>(undefined);

  // isLoading は trigger 用途で deps に含める。送信直後の setIsSending(true) で
  // messages の refetch が走る前にもスクロールを発火させたい。
  // biome-ignore lint/correctness/useExhaustiveDependencies: isLoading は trigger 用途
  useEffect(() => {
    if (messageCount === 0) return;

    // 初回マウントは「スレッドを開いた直後の状態復元」扱いで一律最下部
    const isFirstMount = isInitialRef.current;
    // 直前の assistant id から変わったときだけ新着 AI 応答とみなす（初回マウントは除外）
    const hasNewAssistant = !!lastAssistantId && lastAssistantId !== prevAssistantIdRef.current;
    const shouldAnchorToNewAssistant = !isFirstMount && hasNewAssistant;

    if (shouldAnchorToNewAssistant) {
      lastAssistantRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
    } else {
      bottomMarkerRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
    }

    isInitialRef.current = false;
    prevAssistantIdRef.current = lastAssistantId;
  }, [messageCount, lastAssistantId, isLoading]);

  return { lastAssistantRef, bottomMarkerRef };
};
